import {
  Accordion,
  AccordionItemTriggerProps,
  Icon,
  IconProps,
  TextProps,
} from '@chakra-ui/react';
import React, { FunctionComponent as FC, HTMLAttributes } from 'react';
import { MdChevronRight, MdExpandMore } from 'react-icons/md';
import { MFlex } from './chakra';
import { MTooltip } from './MTooltip';
import MText from './MText';

/**
 * This component is used as the content within an AccordionItem.
 * @example
 * ```
    <Accordion multiple w="100%">
      <AccordionItem
        key={id}
        mt={4}
        borderWidth="1px"
        borderRadius="md"
        borderColor="tGray.back"
      >
        {({ isExpanded }) => (
          <>
            <MAccordionCustomButton isExpanded={isExpanded} label="Test">
              <MAccordionCustomButtonItem label="test:" value="test" />
              <MAccordionCustomButtonItem label="test:" value="test" />
            </MAccordionCustomButton>
            <AccordionPanel>test</AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Accordion>
 * ```
 */

export const isNonButtonTrigger = (as: AccordionItemTriggerProps['as']) =>
  !!as && as !== 'button';

interface MAccordionCustomButtonProps extends HTMLAttributes<HTMLHeadingElement> {
  isExpanded: boolean;
  label: string;
  buttonProps?: AccordionItemTriggerProps;
  children?: React.ReactNode;
  labelProps?: TextProps;
  iconProps?: IconProps;
  testId?: string;
  customLabel?: React.ReactNode;
}

const MAccordionCustomButton: FC<MAccordionCustomButtonProps> = ({
  isExpanded,
  label,
  children,
  buttonProps,
  labelProps,
  testId,
  customLabel,
  iconProps,
  ...rest
}: MAccordionCustomButtonProps) => {
  return (
    <h2 {...rest}>
      <Accordion.ItemTrigger
        py={0}
        pr={8}
        // zag only toggles an item on a click while its trigger has focus. A
        // button takes focus on mousedown; a trigger rendered `as` a div (so
        // header buttons like Edit don't nest in a <button>) never did, so it
        // ignored every click.
        tabIndex={isNonButtonTrigger(buttonProps?.as) ? 0 : undefined}
        {...buttonProps}
        data-testid={testId}
      >
        <MFlex alignItems="center" justifyContent="space-between" w="full">
          <MFlex textAlign="left" alignItems="center" py={3} flexGrow={1}>
            <MTooltip label="Toggle" placement="top-start">
              <MFlex alignItems="center">
                {isExpanded ? (
                  <Icon boxSize="1.5rem" {...iconProps} asChild>
                    <MdExpandMore />
                  </Icon>
                ) : (
                  <Icon boxSize="1.5rem" {...iconProps} asChild>
                    <MdChevronRight />
                  </Icon>
                )}
              </MFlex>
            </MTooltip>
            {customLabel || (
              <MText fontWeight="600" fontSize="lg" ml={1} {...labelProps}>
                {label}
              </MText>
            )}
          </MFlex>
          <MFlex alignItems="center">{children}</MFlex>
        </MFlex>
      </Accordion.ItemTrigger>
    </h2>
  );
};

export default MAccordionCustomButton;
